// Results.js

import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import specialtiesData from '../../../data/SpecialtyOverview.json'; // Adjust the path as necessary
import mbtiData from '../../../data/MBTI_specialties.json'; // Adjust the path as necessary
import mbtiDescriptions from '../../../data/MBTI_descriptions.json'; // Import MBTI descriptions
import personalitySpecialtyDescriptions from '../../../data/PersonalitySpecialtyDescriptions.json'; // Import the new JSON file
import '../../../css/App.css'; // Global styles
import './Results.css'; // Import your specialties styles
import './CompareOverlay.css';
import { 
  FaBalanceScale, 
  FaGraduationCap, 
  FaCalendarAlt, 
  FaBook, 
  FaListUl, 
  FaLayerGroup, 
  FaChartLine,
  FaStethoscope,
  FaTimes,
  FaLightbulb,
  FaInfoCircle,
  FaExclamationTriangle,
  FaHospital,
  FaFlask,
  FaMicroscope,
  FaCertificate,
  FaRegFileAlt
} from 'react-icons/fa';
import { saveMBTIResults } from '../../../api/mbtiQuizApi';
import { getCurrentUser } from 'aws-amplify/auth';

const Results = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedSpecialty, setSelectedSpecialty] = useState(null); // State for the selected specialty
  const [showCompareOverlay, setShowCompareOverlay] = useState(false); // State for compare overlay
  const [compareSpecialty1, setCompareSpecialty1] = useState(null); // State for first specialty in comparison
  const [compareSpecialty2, setCompareSpecialty2] = useState(null); // State for second specialty in comparison
  const [saveStatus, setSaveStatus] = useState({
    saving: false,
    error: null,
    success: false
  });

  const finalScores = location.state?.finalScores;

  // Get specialties based on the user's MBTI type
  const personalityType = finalScores?.personalityType || '';
  const scores = finalScores?.scores || {};

  const personalityDescription = mbtiDescriptions[personalityType] || 'Description not available.';

  // Wrap specialtiesForPersonality in useMemo
  const specialtiesForPersonality = useMemo(() => {
    return mbtiData[personalityType]?.specialties || [];
  }, [personalityType]);

  // Get the first specialty name
  // const firstSpecialtyName = specialtiesForPersonality[0] || 'GP';

  // Initialize chosenSpecialty with firstSpecialtyName if not set
  // const initialChosenSpecialty = chosenSpecialty || firstSpecialtyName;

  // Get list of specialties not in specialtiesForPersonality
  const specialtiesNotListed = specialtiesData.specialties
    .map((specialty) => specialty.name)
    .filter((name) => !specialtiesForPersonality.includes(name));

  // Combine all specialties for dropdowns
  const allSpecialties = specialtiesData.specialties.map((specialty) => specialty.name);

  // Prepopulate compareSpecialty1 and compareSpecialty2
  useEffect(() => {
    if (!compareSpecialty1 && specialtiesForPersonality.length > 0) {
      const specialty = specialtiesData.specialties.find(
        (s) => s.name === specialtiesForPersonality[0]
      );
      setCompareSpecialty1(specialty);
    }
    if (!compareSpecialty2 && specialtiesForPersonality.length > 1) {
      const specialty = specialtiesData.specialties.find(
        (s) => s.name === specialtiesForPersonality[1]
      );
      setCompareSpecialty2(specialty);
    }
  }, [compareSpecialty1, compareSpecialty2, specialtiesForPersonality]);

  useEffect(() => {
    const saveResults = async () => {
      if (!finalScores || !personalityType) {
        console.log('No results to save:', { finalScores, personalityType });
        return;
      }
      
      try {
        setSaveStatus({ saving: true, error: null, success: false });
        console.log('Starting save with:', {
          personalityType,
          specialtiesForPersonality,
          scores
        });
        
        const user = await getCurrentUser();
        if (!user) {
          throw new Error('No authenticated user found');
        }

        const result = await saveMBTIResults(
          personalityType,
          specialtiesForPersonality,
          scores,
          user.username
        );
        
        console.log('Save successful:', result);
        setSaveStatus({ saving: false, error: null, success: true });
      } catch (error) {
        console.error('Save failed:', error);
        setSaveStatus({ 
          saving: false, 
          error: error.message || 'Failed to save results', 
          success: false 
        });
      }
    };

    saveResults();
  }, [finalScores, personalityType, specialtiesForPersonality, scores]);

  // NEW: persist MBTI results locally for use without login
  useEffect(() => {
    try {
      if (personalityType) {
        localStorage.setItem(
          'medmap.mbti.result',
          JSON.stringify({
            personalityType,
            recommendedSpecialties: specialtiesForPersonality || [],
            scores: scores || {}
          })
        );
      }
    } catch (e) {
      console.warn('Failed to cache MBTI results locally:', e);
    }
  }, [personalityType, specialtiesForPersonality, scores]);

  if (!finalScores) {
    return <p>No results available.</p>;
  }

  // Helper function to display the specialty details
  const renderSpecialtyDetails = (specialty) => {
    // Fetch the personalized description
    const specialtyName = specialty.name;
    const personalitySpecialtyDescription =
      personalitySpecialtyDescriptions[personalityType]?.[specialtyName] ||
      'No description available for this specialty and personality combination.';

    return (
      <div className="specialty-details" key={specialty.name}>
        <h3>{specialty.name}</h3>
        <p>
          <strong>Current Curriculum:</strong>{' '}
          {specialty.additional_info?.current_curriculum ? (
            <a href={specialty.additional_info.current_curriculum.link}>
              {specialty.additional_info.current_curriculum.title}
            </a>
          ) : (
            'N/A'
          )}
        </p>
        <p>
          <strong>Core Training Options:</strong>{' '}
          {Array.isArray(specialty.additional_info.core_training_options)
            ? specialty.additional_info.core_training_options.join(', ')
            : 'N/A'}
        </p>
        <p>
          <strong>Indicative Length of Training:</strong>{' '}
          {specialty.additional_info.indicative_length_of_training || 'N/A'}
        </p>
        <p>
          <strong>Exams:</strong> {specialty.additional_info.exams || 'N/A'}
        </p>
        <p>
          <strong>Approved Sub-Specialties:</strong>{' '}
          {Array.isArray(specialty.additional_info.approved_sub_specialties)
            ? specialty.additional_info.approved_sub_specialties.join(', ')
            : 'N/A'}
        </p>
        <p>
          <strong>Approved Dual CCT Pairings:</strong>{' '}
          {Array.isArray(specialty.additional_info.approved_dual_CCT_pairings)
            ? specialty.additional_info.approved_dual_CCT_pairings.join(', ')
            : 'N/A'}
        </p>
        {specialty.additional_info.competition_ratio && (
          <p>
            <strong>Competition Ratio:</strong>{' '}
            {JSON.stringify(specialty.additional_info.competition_ratio)}
          </p>
        )}
        {/* Updated section for 'Why This Specialty Fits Your Personality' */}
        <div className="why-specialty">
          <h4>Why This Specialty Fits Your Personality</h4>
          <p>{personalitySpecialtyDescription}</p>
        </div>
      </div>
    );
  };

  // Function to render the spectrum bars with actual scores next to labels
  const renderSpectrumBar = (label1, label2, score1, score2) => {
    const maxScore = 5; // Assuming maximum score per trait is 5
    const difference = score1 - score2; // Range from -5 to +5
    const percentage = ((difference + maxScore) / (2 * maxScore)) * 100; // Convert to percentage for positioning

    return (
      <div className="spectrum-bar">
        <div className="spectrum-labels">
          <span>
            {label1}: {score1}
          </span>
          <span>
            {label2}: {score2}
          </span>
        </div>
        <div className="spectrum-container">
          <div className="spectrum-bar-center"></div>
          <div
            className="spectrum-indicator"
            style={{ left: `${percentage}%` }}
          ></div>
        </div>
        <div className="spectrum-scale">
          <span>-{maxScore}</span>
          <span>0</span>
          <span>+{maxScore}</span>
        </div>
      </div>
    );
  };

  const handleDropdownChange = (e) => {
    const specialtyName = e.target.value;
    if (specialtyName) {
      const specialty = specialtiesData.specialties.find(
        (s) => s.name === specialtyName
      );
      setSelectedSpecialty(specialty);
    }
  };

  const handleCompareSpecialtyChange = (e, specialtyNumber) => {
    const specialtyName = e.target.value;
    if (specialtyName) {
      const specialty = specialtiesData.specialties.find(
        (s) => s.name === specialtyName
      );
      if (specialtyNumber === 1) {
        setCompareSpecialty1(specialty);
      } else if (specialtyNumber === 2) {
        setCompareSpecialty2(specialty);
      }
    } else {
      if (specialtyNumber === 1) {
        setCompareSpecialty1(null);
      } else if (specialtyNumber === 2) {
        setCompareSpecialty2(null);
      }
    }
  };

  // Function to render the comparison table
  const renderComparisonTable = () => {
    // Define comparison categories with icons
    const comparisonCategories = [
      { key: 'Curriculum', icon: <FaBook className="specialty-compare-category-icon" />, label: 'Current Curriculum' },
      { key: 'Training', icon: <FaFlask className="specialty-compare-category-icon" />, label: 'Core Training Options' },
      { key: 'Duration', icon: <FaCalendarAlt className="specialty-compare-category-icon" />, label: 'Length of Training' },
      { key: 'Exams', icon: <FaGraduationCap className="specialty-compare-category-icon" />, label: 'Required Examinations' },
      { key: 'SubSpec', icon: <FaMicroscope className="specialty-compare-category-icon" />, label: 'Sub-Specialties' },
      { key: 'DualCCT', icon: <FaCertificate className="specialty-compare-category-icon" />, label: 'Dual CCT Pairings' },
      { key: 'CompRatio', icon: <FaChartLine className="specialty-compare-category-icon" />, label: 'Competition Ratio' },
    ];

    // Get data for each specialty
    const getSpecialtyData = (specialty) => ({
      Curriculum: specialty ? (
        specialty.additional_info?.current_curriculum ? (
          <a href={specialty.additional_info.current_curriculum.link} target="_blank" rel="noopener noreferrer">
            {specialty.additional_info.current_curriculum.title}
          </a>
        ) : 'N/A'
      ) : 'Select a specialty',
      
      Training: specialty && Array.isArray(specialty.additional_info.core_training_options)
        ? specialty.additional_info.core_training_options.length > 0
          ? specialty.additional_info.core_training_options.join(', ')
          : 'Direct entry (no core requirement)'
        : 'Select a specialty',
      
      Duration: specialty
        ? specialty.additional_info.indicative_length_of_training || 'Not specified'
        : 'Select a specialty',
      
      Exams: specialty
        ? Array.isArray(specialty.additional_info.exams)
          ? specialty.additional_info.exams.join(', ')
          : (specialty.additional_info.exams || 'None specified')
        : 'Select a specialty',
      
      SubSpec: specialty && Array.isArray(specialty.additional_info.approved_sub_specialties)
        ? specialty.additional_info.approved_sub_specialties.length > 0
          ? specialty.additional_info.approved_sub_specialties.join(', ')
          : 'None available'
        : 'Select a specialty',
      
      DualCCT: specialty && Array.isArray(specialty.additional_info.approved_dual_CCT_pairings)
        ? specialty.additional_info.approved_dual_CCT_pairings.length > 0
          ? specialty.additional_info.approved_dual_CCT_pairings.join(', ')
          : 'None available'
        : 'Select a specialty',
      
      CompRatio: specialty && specialty.additional_info.competition_ratio
        ? typeof specialty.additional_info.competition_ratio === 'object'
          ? Object.entries(specialty.additional_info.competition_ratio).map(([key, value]) => {
              if (typeof value === 'object' && value.ratio) {
                return `${key}: ${value.ratio.toFixed(2)} (${value.applications}/${value.posts})`;
              } else if (key !== 'applications' && key !== 'posts') {
                return `${key}: ${value}`;
              }
              return null;
            }).filter(Boolean).join(', ')
          : JSON.stringify(specialty.additional_info.competition_ratio)
        : 'Data not available',
    });

    const specialty1Data = getSpecialtyData(compareSpecialty1);
    const specialty2Data = getSpecialtyData(compareSpecialty2);
    
    const bothSpecialtiesSelected = compareSpecialty1 && compareSpecialty2;

    // Check if specialties are selected
    if (!compareSpecialty1 && !compareSpecialty2) {
      return (
        <div className="specialty-compare-empty">
          Please select specialties to compare their details
        </div>
      );
    }

    return (
      <div className="specialty-compare-section">
        <div className="specialty-compare-table-wrapper">
          <table className="specialty-compare-table">
            <thead className="specialty-compare-header-row">
              <tr>
                <th className="specialty-compare-header-cell">Feature</th>
                <th className="specialty-compare-header-cell">
                  {compareSpecialty1?.name || 'Select Specialty 1'}
                </th>
                <th className="specialty-compare-header-cell">
                  {compareSpecialty2?.name || 'Select Specialty 2'}
                </th>
              </tr>
            </thead>
            <tbody>
              {comparisonCategories.map((category) => {
                const isDifferent = bothSpecialtiesSelected && 
                  specialty1Data[category.key] !== specialty2Data[category.key];
                
                return (
                  <tr key={category.key} className="specialty-compare-row">
                    <td className="specialty-compare-category">
                      {category.icon} {category.label}
                    </td>
                    <td className={`specialty-compare-value ${isDifferent ? 'specialty-compare-highlight' : ''}`}>
                      {specialty1Data[category.key]}
                    </td>
                    <td className={`specialty-compare-value ${isDifferent ? 'specialty-compare-highlight' : ''}`}>
                      {specialty2Data[category.key]}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {bothSpecialtiesSelected && (
          <div className="specialty-compare-warning">
            <FaExclamationTriangle className="specialty-compare-warning-icon" />
            <div className="specialty-compare-warning-content">
              <strong>Note:</strong> Highlighted rows indicate differences between the two specialties.
              These differences may be important factors in your specialty decision.
            </div>
          </div>
        )}
        
        <div className="specialty-compare-tip">
          <FaLightbulb className="specialty-compare-tip-icon" />
          <div className="specialty-compare-tip-content">
            <strong>Tip:</strong> Consider not only the technical aspects like exams and training duration,
            but also how each specialty aligns with your MBTI personality type and personal preferences.
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="results-root">
      {/* Hero */}
      <section className="results-hero">
        <div className="results-hero-inner">
          <h1 className="results-hero-title">
            Your <span className="results-gradient-text">MBTI Results</span>
          </h1>
          <p className="results-hero-sub">
            Personality type: <strong>{personalityType || '—'}</strong>. Explore your fit across suggested specialties and compare training pathways side-by-side.
          </p>
        </div>
      </section>

      {/* Content */}
      <div className="results-container">
        {/* Panel 1 */}
        <div className="panel">
          <div className="panel-header">
            <div className="panel-number">1</div>
            <h2>Your MBTI Personality Type: {personalityType}</h2>
          </div>
          <div className="panel-content">
            <h3>Personality Type Description</h3>
            <p className="personality-description">{personalityDescription}</p>

            <h3>Score Breakdown:</h3>
            {renderSpectrumBar('Extroversion (E)', 'Introversion (I)', scores.E, scores.I)}
            {renderSpectrumBar('Sensing (S)', 'Intuition (N)', scores.S, scores.N)}
            {renderSpectrumBar('Thinking (T)', 'Feeling (F)', scores.T, scores.F)}
            {renderSpectrumBar('Judging (J)', 'Perceiving (P)', scores.J, scores.P)}
          </div>
        </div>

        {/* Panel 2 */}
        <div className="panel">
          <div className="panel-header">
            <div className="panel-number">2</div>
            <h2>Suggested Specialties for {personalityType}</h2>
          </div>
          <div className="panel-content specialties-panel">
            <div className="specialties-list">
              {specialtiesForPersonality.length > 0 ? (
                specialtiesForPersonality.map((specialtyName, index) => {
                  const specialty = specialtiesData.specialties.find(
                    (s) => s.name === specialtyName
                  );
                  return (
                    <div
                      key={specialtyName}
                      className={`specialty-item ${
                        selectedSpecialty && selectedSpecialty.name === specialtyName
                          ? 'selected'
                          : ''
                      }`}
                      onClick={() => setSelectedSpecialty(specialty)}
                    >
                      <span className="specialty-number">{index + 1}</span>
                      <span className="specialty-name">{specialtyName}</span>
                    </div>
                  );
                })
              ) : (
                <p>No specialties available for this personality type.</p>
              )}
              {/* Dropdown for specialties not listed above */}
              <div className="specialty-dropdown">
                <label htmlFor="other-specialty">
                  Choose Specialty Not Listed Above:
                </label>
                <select id="other-specialty" onChange={handleDropdownChange}>
                  <option value="">Select a Specialty</option>
                  {specialtiesNotListed.map((name, index) => (
                    <option key={index} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {selectedSpecialty && (
              <div className="specialty-details-panel">
                {renderSpecialtyDetails(selectedSpecialty)}
              </div>
            )}
          </div>
          <div className="compare-choose-buttons">
            <button
              className="compare-specialties-button"
              onClick={() => setShowCompareOverlay(true)}
            >
              Compare Specialties
            </button>
            <button
              className="choose-specialty-button"
              onClick={() => navigate('/dream-specialty')}
            >
              Continue to Dream Specialty
            </button>
          </div>
        </div>

        {/* Compare Overlay */}
        {showCompareOverlay && (
          <div className="specialty-compare-overlay">
            <div className="specialty-compare-container">
              <div className="specialty-compare-header">
                <h2 className="specialty-compare-title">
                  <FaBalanceScale className="specialty-compare-title-icon" />
                  Compare Medical Specialties
                </h2>
                <button
                  className="specialty-compare-close"
                  onClick={() => setShowCompareOverlay(false)}
                  aria-label="Close comparison"
                >
                  <FaTimes />
                </button>
              </div>
              
              <div className="specialty-compare-body">
                <p className="specialty-compare-description">
                  Compare training requirements, exams, and other key aspects of medical specialties to help 
                  inform your career decisions. Select two specialties below to see their differences.
                </p>
                
                <div className="specialty-select-grid">
                  <div className="specialty-select-column">
                    <label className="specialty-select-label" htmlFor="specialty-select-1">
                      Specialty 1
                    </label>
                    <FaStethoscope className="specialty-select-icon" />
                    <select
                      id="specialty-select-1"
                      className="specialty-select-control"
                      value={compareSpecialty1 ? compareSpecialty1.name : ''}
                      onChange={(e) => handleCompareSpecialtyChange(e, 1)}
                    >
                      <option value="">Select a specialty</option>
                      {allSpecialties.map((name, index) => (
                        <option key={index} value={name}>
                          {name}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="specialty-select-column">
                    <label className="specialty-select-label" htmlFor="specialty-select-2">
                      Specialty 2
                    </label>
                    <FaStethoscope className="specialty-select-icon" />
                    <select
                      id="specialty-select-2"
                      className="specialty-select-control"
                      value={compareSpecialty2 ? compareSpecialty2.name : ''}
                      onChange={(e) => handleCompareSpecialtyChange(e, 2)}
                    >
                      <option value="">Select a specialty</option>
                      {allSpecialties.map((name, index) => (
                        <option key={index} value={name}>
                          {name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                
                {renderComparisonTable()}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Results;